import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { AiNocComponent } from './ai-noc/ai-noc.component';
import { CohortsComponent } from './cohorts/cohorts.component';
import { SettingsTypeResolver } from './core/settings/settings-type.resolver';
import { DashboardsComponent } from './dashboards/dashboards.component';
import { DeviceCommandComponent } from './device-command/device-command.component';
import { DeviceHealthComponent } from './device-health/device-health.component';
import { MapComponent } from './fleet-tracker/map/map.component';
import { FeatureToggleGuard } from './guards/feature-toggle.guard';
import { PermissionGuard } from './guards/permission.guard';
import { RouteStateGuard } from './guards/route-state.guard';
import { InsightsPageComponent } from './insights/insights-page.component';
import { ModulesInsightsComponent } from './insights/pages/apps/modules-insights.component';
import { DeviceBatteryInsightsComponent } from './insights/pages/shadow-battery/device-battery-insights.component';
import { DevicesInsightsComponent } from './insights/pages/shadow-devices/devices-insights.component';
import { IgnitionInsightsComponent } from './insights/pages/shadow-ignition/ignition-insights.component';
import { OverdueInsightsComponent } from './insights/pages/shadow-overdue/overdue-insights.component';
import { FirmwareDetailsComponent } from './modules/firmware-details/firmware-details.component';
import { ModuleDetailsComponent } from './modules/module-details/module-details.component';
import { ModulesComponent } from './modules/modules.component';
import { DevicemonitoringComponent } from './monitoring/device-monitoring/devicemonitoring.component';
import { DeviceProfilePageComponent } from './monitoring/device-profile-page/device-profile-page.component';
import { OffVpnComponent } from './off-vpn/off-vpn.component';
import { OffVpnGuard } from './off-vpn/off-vpn.guard';
import { ReleasesComponent } from './releases/releases.component';
import { RequestGridComponent } from './request-grid/request-grid.component';
import { NestedTitleStrategy } from './services/nested-title-strategy.service';
import { SettingsHierarchyComponent } from './settings/hierarchy/settings-hierarchy.component';
import { SettingsRequestsComponent } from './settings/requests/settings-requests.component';
import { SettingsComponent } from './settings/settings.component';
import { SettingsTypesComponent } from './settings/settings-types/settings-types.component';
import { SettingsTypesAuditComponent } from './settings/settings-types-audit/settings-types-audit.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: '/statusreport',
    pathMatch: 'full',
  },
  {
    path: 'statusreport',
    title: 'Status Report',
    pathMatch: 'full',
    component: DeviceHealthComponent,
    canActivate: [OffVpnGuard],
  },
  // Declare this BEFORE the `insights` parent route. Angular routing matches
  // the first definition whose `path` accepts the URL prefix, so if the
  // parent `insights` route is listed first it will consume `/insights/...`
  // and then fail to find a child matching `device/:serialNumber`, leaving
  // this route unreachable.
  {
    path: 'insights/device/:serialNumber',
    title: 'Device Profile',
    component: DeviceProfilePageComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'insights',
    title: 'Insights',
    component: InsightsPageComponent,
    canActivate: [OffVpnGuard, RouteStateGuard],
    children: [
      {
        path: 'devices',
        title: 'Devices',
        pathMatch: 'full',
        component: DevicesInsightsComponent,
      },
      {
        path: 'no-ignition',
        title: 'No Ignition',
        pathMatch: 'full',
        component: IgnitionInsightsComponent,
      },
      {
        path: 'device-battery',
        title: 'Device Battery',
        pathMatch: 'full',
        component: DeviceBatteryInsightsComponent,
        canActivate: [FeatureToggleGuard],
        data: { featureToggle: 'enableBatteryView' },
      },
      {
        path: 'overdue-for-download',
        title: 'Overdue for Download',
        pathMatch: 'full',
        component: OverdueInsightsComponent,
      },
      {
        path: 'apps',
        title: 'Apps',
        pathMatch: 'full',
        component: ModulesInsightsComponent,
      },
    ],
  },
  {
    path: 'insights/device/:serialNumber',
    title: 'Device Profile',
    component: DeviceProfilePageComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'profiles',
    title: 'Profiles',
    pathMatch: 'full',
    component: DevicemonitoringComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'map',
    title: 'Map',
    pathMatch: 'full',
    component: MapComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'syslogs',
    title: 'Syslogs',
    pathMatch: 'full',
    component: RequestGridComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'settings',
    pathMatch: 'full',
    redirectTo: 'settings/hierarchy/0/current',
  },
  {
    path: 'settings/hierarchy',
    pathMatch: 'full',
    redirectTo: 'settings/hierarchy/0/current',
  },
  {
    path: 'settings',
    title: 'Settings',
    component: SettingsComponent,
    canActivate: [OffVpnGuard],
    resolve: {
      settingType: SettingsTypeResolver,
    },
    children: [
      {
        title: 'Hierarchy',
        path: 'hierarchy/:selectedEntityId/:settingsView',
        component: SettingsHierarchyComponent,
      },
      {
        title: 'Requests',
        path: 'requests',
        component: SettingsRequestsComponent,
      },
      {
        title: 'Types',
        path: 'types',
        component: SettingsTypesComponent,
        canActivate: [PermissionGuard],
        data: {
          permissions: ['SettingTypeAdmin'],
        },
      },
      {
        title: 'Audit',
        path: 'types/:name/audit',
        component: SettingsTypesAuditComponent,
        canActivate: [PermissionGuard],
        data: {
          permissions: ['SettingTypeAdmin'],
        },
      },
    ],
  },
  {
    path: 'ai-noc',
    title: 'AI NOC',
    pathMatch: 'full',
    component: AiNocComponent,
    canActivate: [OffVpnGuard, PermissionGuard],
    data: {
      permissions: [
        'DeviceMonitoringWriter',
        'DeviceMonitoringReader',
        'DeviceMonitoringAdmin',
      ],
    },
  },
  {
    path: 'commands',
    title: 'Commands',
    pathMatch: 'full',
    component: DeviceCommandComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'dashboards',
    title: 'Dashboards',
    pathMatch: 'full',
    component: DashboardsComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'cohorts',
    title: 'Cohorts',
    pathMatch: 'full',
    component: CohortsComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'releases',
    title: 'Releases',
    pathMatch: 'full',
    component: ReleasesComponent,
    canActivate: [OffVpnGuard],
  },
  {
    path: 'modules',
    title: 'Modules',
    canActivate: [OffVpnGuard],
    children: [
      {
        path: '',
        title: 'Modules',
        pathMatch: 'full',
        component: ModulesComponent,
      },
      {
        path: 'firmware',
        title: 'Details',
        pathMatch: 'full',
        component: FirmwareDetailsComponent,
      },
      {
        path: ':name',
        title: 'Details',
        pathMatch: 'full',
        component: ModuleDetailsComponent,
      },
    ],
  },
  {
    path: 'off-vpn',
    component: OffVpnComponent,
    title: 'Off VPN',
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: NestedTitleStrategy }],
})
export class AppRoutingModule {}
